import { PERMISSIONS, PERMISSION_ACTION_LABELS, type PermissionType } from './access-control.js';
import { START_AS_IF_POLICY_SECTION } from './start-as-if-policy.js';

/**
 * The permissions an as-if credential never holds, whatever the role it views as has (F-0709,
 * `docs/concept-start-a-work-area-as-if.md` §2.3, decided by Jay on D-0396).
 *
 * A viewer "as Owner" holds the Owner's rights capped by their own, and then this list is taken away.
 * The reason is one of two: the act is a **signature or a bulk extraction** (`decision.answer`,
 * `work_area.export`), or it changes **who has access or who can act** (the rest), and those are done
 * as oneself. `as_if.allow` is on the list so a person viewing as another role cannot change what that
 * view can do (the reason `trust_source.allow_agents` is on it too). `as_if.start` needs no entry: the
 * mint refuses an as-if credential that asks to start another (ASIF-2, ASIF-11).
 *
 * ⛔ Every entry has its own case in `as-if-ceiling.test.ts` (ASIF-17 to ASIF-26, ASIF-28), and the
 * test fails when the list and the cases differ (ASIF-27). Adding an entry is one reviewed line.
 */
export const ASIF_CEILING_DENIED = [
  'decision.answer',
  'work_area.export',
  'work_area.publish',
  'verification.mint',
  'user.manage',
  'installation.manage',
  'role.assign',
  'role.manage',
  'agent.create',
  'trust_source.allow_agents',
  'as_if.allow',
  // F-0713: who may answer requests for agent access is not changed while viewing as another role.
  'access_request.allow_agents',
] as const satisfies readonly PermissionType[];
export type AsIfCeilingDeniedType = (typeof ASIF_CEILING_DENIED)[number];

/** Every permission an as-if credential may hold at most: the whole vocabulary less the ceiling. */
export const ASIF_CEILING_ALLOWED: readonly PermissionType[] = PERMISSIONS.filter(
  (permission) => !isAsIfCeilingDenied(permission),
);

export function isAsIfCeilingDenied(permission: PermissionType): boolean {
  return (ASIF_CEILING_DENIED as readonly PermissionType[]).includes(permission);
}

/**
 * `effective = holder's own ∩ the target's ∩ the ceiling`: what an as-if credential holds in the
 * bound work area. Built by filtering the **holder's** permissions, so nothing the holder does not
 * hold can survive it: a view can only narrow, never raise (ADR-051's argument, one more factor).
 */
export function asIfEffectivePermissions(
  holder: readonly PermissionType[],
  target: readonly PermissionType[],
): readonly PermissionType[] {
  const wanted = new Set(target);
  return [...new Set(holder)].filter(
    (permission) => wanted.has(permission) && !isAsIfCeilingDenied(permission),
  );
}

/**
 * What the target holds that the viewer cannot use: the permissions the banner counts ("limited to
 * your own rights (2 actions)", ASIF-16). The ceiling's entries count too, with their own reason.
 */
export function asIfLimitedPermissions(
  holder: readonly PermissionType[],
  target: readonly PermissionType[],
): readonly PermissionType[] {
  const effective = new Set(asIfEffectivePermissions(holder, target));
  return [...new Set(target)].filter((permission) => !effective.has(permission));
}

/**
 * The reason a control outside the as-if rights is disabled with, never hidden: the act, and that it
 * is done as oneself. One sentence; the catalogue carries it in English and German (web, slice 4).
 */
export function asIfCeilingReason(permission: PermissionType, roleName: string): string {
  return `Not while viewing as ${roleName}: to ${PERMISSION_ACTION_LABELS[permission]} is done as yourself.`;
}

/** The refusal of the door that asks `permission` of an as-if credential, 403, nothing changed. */
export function asIfCeilingRefusal(permission: PermissionType, roleName: string): string {
  return `${asIfCeilingReason(permission, roleName)} Nothing was changed.`;
}

/** Whether the real credential's permissions allow starting as-if (`as_if.start`). */
export function mayStartAsIf(permissions: readonly PermissionType[]): boolean {
  return permissions.includes('as_if.start');
}

/** The `code` of the 403 a ceiling door answers an as-if credential with (D-0396). */
export const AS_IF_CEILING_DENIED_CODE = 'as-if-ceiling-denied';

/** A REST route that is a door of one ceiling permission: refused with that permission's own reason. */
export interface IAsIfCeilingDoor {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** The route as Nest registers it, without the global prefix or a leading slash. */
  path: string;
  permission: AsIfCeilingDeniedType;
}

/**
 * The doors a ceiling permission is asked at that a route identifies by itself, so the refusal says the
 * ceiling's reason ("Not while viewing as Owner: to export a work area is done as yourself") and not the
 * generic "not here". Every entry is a route the default deny already refuses; this only chooses the
 * sentence, and the register test (`as-if-routes.guard.spec.ts`) holds it to the controllers and to the
 * allowlist. Doors a route alone cannot identify (`PATCH work-areas/:id` carries the Public switch and the
 * as-if setting in its body) are named by {@link asIfCeilingBodyDoorOf}, which reads the body (ASIF-19).
 */
export const AS_IF_CEILING_DOORS: readonly IAsIfCeilingDoor[] = [
  { method: 'GET', path: 'work-areas/:projectId/export', permission: 'work_area.export' },
  { method: 'GET', path: 'projects/:projectId/export', permission: 'work_area.export' },
  { method: 'POST', path: 'access-control/invitations', permission: 'role.assign' },
  { method: 'POST', path: 'access-control/invitations/:id/resend', permission: 'role.assign' },
  { method: 'POST', path: 'access-control/bindings', permission: 'role.assign' },
  { method: 'PATCH', path: 'access-control/bindings/:id', permission: 'role.assign' },
  { method: 'DELETE', path: 'access-control/bindings/:id', permission: 'role.assign' },
  { method: 'PUT', path: 'access-control/roles/:id/permissions', permission: 'role.manage' },
  { method: 'GET', path: 'users', permission: 'user.manage' },
  { method: 'PATCH', path: 'users/:id/role', permission: 'user.manage' },
  { method: 'PATCH', path: 'users/:id/deactivated', permission: 'user.manage' },
  { method: 'POST', path: 'users/:id/deletion', permission: 'user.manage' },
  { method: 'DELETE', path: 'users/:id/deletion', permission: 'user.manage' },
  { method: 'GET', path: 'users/:id/export', permission: 'user.manage' },
  { method: 'POST', path: 'agents', permission: 'agent.create' },
  { method: 'POST', path: 'auth/verify-session/mint', permission: 'verification.mint' },
  { method: 'PUT', path: 'system-settings/mail', permission: 'installation.manage' },
  { method: 'POST', path: 'system-settings/mail/test', permission: 'installation.manage' },
  {
    method: 'PUT',
    path: 'work-areas/:workAreaId/access-request-approvers',
    permission: 'access_request.allow_agents',
  },
];

/** The GraphQL mutations that are doors of a ceiling permission, by field. */
export const AS_IF_CEILING_OPERATIONS: ReadonlyArray<{
  field: string;
  permission: AsIfCeilingDeniedType;
}> = [
  { field: 'answerDecision', permission: 'decision.answer' },
  { field: 'unanswerDecision', permission: 'decision.answer' },
  { field: 'claimDecision', permission: 'decision.answer' },
  { field: 'refuseDecision', permission: 'decision.answer' },
  // F-0807: asking for a revision is the responsible's move in place of an answer.
  { field: 'requestDecisionRevision', permission: 'decision.answer' },
  { field: 'setAgentsMayPauseTrustSources', permission: 'trust_source.allow_agents' },
];

/** The ceiling permission a REST request is a door of, or null. */
export function asIfCeilingDoorOf(
  method: string | undefined,
  routePath: string | undefined,
): AsIfCeilingDeniedType | null {
  if (!method || !routePath) return null;
  const path = routePath.replace(/^\/+|\/+$/g, '').replace(/^api\//, '');
  return (
    AS_IF_CEILING_DOORS.find((door) => door.method === method.toUpperCase() && door.path === path)
      ?.permission ?? null
  );
}

const BODY_DOOR_ROUTES: readonly string[] = ['work-areas/:id', 'projects/:id'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The ceiling permission a `PATCH` of a work area is a door of by what its body asks for (ASIF-19): making
 * it Public, offering it to search engines or changing what it publishes is `work_area.publish`, and changing the as-if setting (`policies` or `policiesPatch`
 * carrying `startAsIf`) is `as_if.allow`. Any other body on those routes is no door, and keeps the
 * generic refusal of everything not listed.
 */
export function asIfCeilingBodyDoorOf(
  method: string | undefined,
  routePath: string | undefined,
  body: unknown,
): AsIfCeilingDeniedType | null {
  if (method?.toUpperCase() !== 'PATCH' || !routePath || !isRecord(body)) return null;
  const path = routePath.replace(/^\/+|\/+$/g, '').replace(/^api\//, '');
  if (!BODY_DOOR_ROUTES.includes(path)) return null;
  // What the service asks `work_area.publish` for (`projects.service.ts`, update): the flip to Public,
  // offering the work area to search engines (`indexable: true`; turning it off needs no more than managing
  // the work area), and changing what a Public work area publishes (`publishByDefault`, either value: the
  // service compares it with the stored one, which the gate does not read).
  if (
    body.visibility === 'public' ||
    body.indexable === true ||
    typeof body.publishByDefault === 'boolean'
  ) {
    return 'work_area.publish';
  }
  const asksForStartAsIf = [body.policies, body.policiesPatch].some(
    (policies) => isRecord(policies) && START_AS_IF_POLICY_SECTION in policies,
  );
  return asksForStartAsIf ? 'as_if.allow' : null;
}

/** The ceiling permission a GraphQL operation is a door of, or null. */
export function asIfCeilingOperationOf(field: string | undefined): AsIfCeilingDeniedType | null {
  return (
    AS_IF_CEILING_OPERATIONS.find((operation) => operation.field === field)?.permission ?? null
  );
}

const AS_IF_ROLE_NAMES: Readonly<Record<string, string>> = {
  'work-area-viewer': 'Viewer',
  'work-area-contributor': 'Contributor',
  'work-area-manager': 'Manager',
  'work-area-admin': 'Admin',
  'work-area-owner': 'Owner',
  'work-area-customer': 'Customer',
  'work-area-tester': 'Tester',
  'work-area-partner': 'Partner',
};

/** What a view is as, in the ceiling's sentence: the standard role's name, "a named member", or "another role". */
export function asIfViewName(binding: {
  roleKey: string | null;
  memberUserId: string | null;
}): string {
  if (binding.memberUserId) return 'a named member';
  return (binding.roleKey ? AS_IF_ROLE_NAMES[binding.roleKey] : undefined) ?? 'another role';
}
